import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, MapPin, Phone, Users, Building2,
  Home as HomeIcon,
} from "lucide-react";
import { useSEO, breadcrumbSchema, faqPageSchema, locationHubSchema, SITE_URL } from "../lib/seo";
import { STATES, CITIES } from "../data/nationalArchitecture";
import { SERVICE_BY_SLUG } from "../data/services";
import { trackPhoneClick } from "../lib/tracking";

const CAPABILITY_LABEL = {
  hub: { label: "Active hub", tone: "green" },
  serviced: { label: "National service capability", tone: "muted" },
  capability: { label: "National service capability", tone: "muted" },
};

export default function CityHubPage() {
  const { state: stateSlug, city: citySlug } = useParams();
  const state = STATES[stateSlug];
  const c = CITIES[citySlug];

  useSEO({
    title: c
      ? `NDIS Provider ${c.name} | Alpha Community Care ${state?.code || ""}`.trim()
      : "NDIS Provider Australia | Alpha Community Care",
    description: c ? `${c.intro} ${c.positioning || ""}`.slice(0, 240) : "",
    keywords: c ? [
      `NDIS provider ${c.name}`,
      `disability support ${c.name}`,
      `SIL ${c.name}`,
      `support coordination ${c.name}`,
      `NDIS ${c.name} ${state?.code || ""}`.trim(),
    ] : [],
    canonical: c && state ? `${SITE_URL}/locations/${state.slug}/${c.slug}` : `${SITE_URL}/locations`,
    jsonLd: c && state ? [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Locations", path: "/locations" },
        { name: state.name, path: `/locations/${state.slug}` },
        { name: c.name, path: `/locations/${state.slug}/${c.slug}` },
      ]),
      faqPageSchema(c.faqs),
      ...(c.capability === "hub" ? [
        locationHubSchema({
          name: c.name,
          state: state.code,
          region: state.name,
          suburb: c.suburb,
          postalCode: c.postalCode,
          latitude: c.lat,
          longitude: c.lon,
          slug: `locations/${state.slug}/${c.slug}`,
        }),
      ] : []),
    ] : [],
  });

  if (!c || !state || c.stateSlug !== state.slug) return <Navigate to="/locations" replace />;

  const cap = CAPABILITY_LABEL[c.capability];
  const isHub = c.capability === "hub";

  return (
    <div data-testid={`page-city-${c.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden alpha-gradient-hero alpha-noise border-b border-[color:var(--alpha-line)]">
        <div className="alpha-orb alpha-orb-purple w-[320px] h-[320px] -left-16 top-10 opacity-70 hidden md:block" aria-hidden />
        <div className="alpha-container pt-10 pb-12 lg:pt-16 lg:pb-16 relative">
          <div className="flex items-center gap-2 text-sm text-[color:var(--alpha-mute)]">
            <Link to="/locations" className="hover:text-[color:var(--alpha-purple-700)]">Locations</Link>
            <span className="opacity-50">/</span>
            <Link to={`/locations/${state.slug}`} className="hover:text-[color:var(--alpha-purple-700)]">{state.name}</Link>
            <span className="opacity-50">/</span>
            <span className="text-[color:var(--alpha-ink)] font-semibold">{c.name}</span>
          </div>

          <div className="mt-6">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--alpha-line-strong)] bg-white text-xs font-semibold ${isHub ? "text-[color:var(--alpha-green-700)]" : "text-[color:var(--alpha-purple-700)]"}`}>
              <ShieldCheck className="w-3.5 h-3.5" /> {cap.label} · {c.name}, {state.code}
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[56px] font-display font-medium tracking-tight leading-[1.05] max-w-4xl">
              NDIS provider in <span className="alpha-serif-italic alpha-gradient-text">{c.name}</span>.
            </h1>
            <p className="mt-5 max-w-3xl text-lg text-[color:var(--alpha-mute)]">{c.tagline}.</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="alpha-section">
        <div className="alpha-container max-w-4xl">
          <div className="alpha-overline">About Alpha in {c.name}</div>
          <p className="mt-4 text-lg text-[color:var(--alpha-ink)] leading-relaxed">{c.intro}</p>
          {c.positioning && (
            <p className="mt-4 text-lg text-[color:var(--alpha-mute)] leading-relaxed">{c.positioning}</p>
          )}
        </div>
      </section>

      {/* Hub-specific: address + team card */}
      {isHub && c.hubDetails && (
        <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
          <div className="alpha-container grid md:grid-cols-3 gap-5">
            <div className="alpha-card p-6" data-testid={`city-${c.slug}-hub-address`}>
              <div className="alpha-overline">Operations address</div>
              <div className="mt-3 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[color:var(--alpha-purple-700)] mt-0.5 shrink-0" />
                <div className="font-display text-base font-semibold">{c.hubDetails.address}</div>
              </div>
            </div>
            <div className="alpha-card p-6" data-testid={`city-${c.slug}-hub-team`}>
              <div className="alpha-overline">Team on the ground</div>
              <div className="mt-3 flex items-start gap-3">
                <Users className="w-5 h-5 text-[color:var(--alpha-purple-700)] mt-0.5 shrink-0" />
                <div className="text-sm text-[color:var(--alpha-ink)]">{c.hubDetails.teamSize}</div>
              </div>
            </div>
            <div className="alpha-card p-6" data-testid={`city-${c.slug}-hub-response`}>
              <div className="alpha-overline">Response time</div>
              <div className="mt-3 flex items-start gap-3">
                <Phone className="w-5 h-5 text-[color:var(--alpha-purple-700)] mt-0.5 shrink-0" />
                <div className="text-sm text-[color:var(--alpha-ink)]">{c.hubDetails.responseTime}</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      <section className={`alpha-section ${isHub ? "" : "bg-[color:var(--alpha-bg-soft)]"}`}>
        <div className="alpha-container">
          <div className="alpha-overline">NDIS services in {c.name}</div>
          <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05] max-w-2xl">
            What Alpha genuinely offers in {c.name}.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.services.map((svc) => {
              const base = SERVICE_BY_SLUG[svc.slug];
              const Icon = base?.Icon || HomeIcon;
              return (
                <Link key={svc.slug} to={`/services/${svc.slug}`} className="alpha-card p-6 group hover:-translate-y-1 transition-transform" data-testid={`city-${c.slug}-svc-${svc.slug}`}>
                  <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center group-hover:bg-[color:var(--alpha-purple-700)] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">{svc.label}</h3>
                  <p className="mt-2 text-sm text-[color:var(--alpha-mute)]">{svc.note}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Suburbs (hubs only) */}
      {isHub && c.servicedSuburbs?.length > 0 && (
        <section className="alpha-section">
          <div className="alpha-container">
            <div className="alpha-overline">Suburbs we service in {c.name}</div>
            <h2 className="mt-3 text-3xl font-display font-medium">Across the {c.name} metro area.</h2>
            <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">A sample of the suburbs we regularly deliver into. Not on this list? Call — we'll assess honestly.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.servicedSuburbs.map((sub) => (
                <span key={sub} className="alpha-chip-neutral alpha-chip" data-testid={`city-${c.slug}-suburb-${sub.toLowerCase().replace(/\s+/g, "-")}`}>
                  <MapPin className="w-3 h-3" /> {sub}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Homes in this city */}
      {c.homes?.length > 0 && (
        <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
          <div className="alpha-container">
            <div className="alpha-overline">Alpha homes in {c.name}</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05] max-w-2xl">
              Real homes with real vacancies.
            </h2>
            <div className="mt-8 grid md:grid-cols-2 gap-5">
              {c.homes.map((h) => (
                <Link key={h.slug} to={`/homes/${h.slug}`} className="alpha-card p-7 group" data-testid={`city-${c.slug}-home-${h.slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl alpha-gradient text-white flex items-center justify-center">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display text-xl font-semibold">{h.label}</div>
                      <div className="text-xs text-[color:var(--alpha-mute)] mt-0.5">{h.suburb}, {state.code}</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:underline">Explore this home <ArrowRight className="w-3.5 h-3.5 inline" /></div>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/vacancies" className="alpha-btn-ghost text-sm" data-testid={`city-${c.slug}-view-vacancies`}>
                View all vacancies <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Proximity (hubs only) */}
      {isHub && c.proximity && (
        <section className="alpha-section">
          <div className="alpha-container max-w-4xl">
            <div className="alpha-overline">Location & community</div>
            <h2 className="mt-3 font-display text-3xl font-medium">Well-connected across {c.name}.</h2>
            <p className="mt-4 text-[color:var(--alpha-mute)] leading-relaxed">{c.proximity}</p>
          </div>
        </section>
      )}

      {/* Referral */}
      <section className={`alpha-section ${isHub ? "bg-[color:var(--alpha-bg-soft)]" : ""}`}>
        <div className="alpha-container grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-6">
            <div className="alpha-overline">Referring in {c.name}</div>
            <h2 className="mt-3 font-display text-3xl font-medium">Check availability in {c.name}.</h2>
            <p className="mt-4 text-[color:var(--alpha-mute)] leading-relaxed">{c.referral}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/referrals/participant" className="alpha-btn-primary">Participant / family <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/referrals/coordinator" className="alpha-btn-green">Coordinator / provider <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="lg:col-span-6 alpha-card p-6 border-[color:var(--alpha-line-strong)] bg-white">
            <div className="alpha-overline">Prefer to talk?</div>
            <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `city-${c.slug}`)} className="mt-3 font-display text-3xl font-semibold alpha-gradient-text inline-flex items-center gap-2">
              <Phone className="w-6 h-6" /> 1300 380 692
            </a>
            <p className="mt-4 text-sm text-[color:var(--alpha-mute)]">
              {isHub
                ? `Our ${c.name} team responds inside 2 business hours during business days.`
                : `Our national intake team handles ${c.name} enquiries — with honest, timely feedback.`}
            </p>
          </div>
        </div>
      </section>

      {/* City FAQs */}
      <section className="alpha-section">
        <div className="alpha-container max-w-3xl">
          <div className="alpha-overline">{c.name} FAQs</div>
          <h2 className="mt-3 font-display text-3xl font-medium">Common {c.name} questions.</h2>
          <div className="mt-6 space-y-3">
            {c.faqs.map((f, i) => (
              <details key={f.q} className="alpha-card p-0 overflow-hidden" data-testid={`city-${c.slug}-faq-${i}`}>
                <summary className="list-none p-5 flex items-center justify-between cursor-pointer">
                  <span className="font-display text-base font-semibold pr-4">{f.q}</span>
                  <span className="text-[color:var(--alpha-purple-600)] font-bold">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
